import React, { memo, useCallback, useEffect, useState } from 'react';

import { useParams, Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { History, LocationState } from 'history';
import { Glyphicon } from 'react-bootstrap';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { Itinerary } from '../../../../types/Itinerary';
import CollapsableItem from '../../../../components/utils/CollapsableItem';
import { isMobileResolution } from '../../../../infrastructure/utils/common';
import DragnDropTable from '../../../../components/utils/DragnDrop/DragnDropTable';
import { getCityBySlug } from '../../../../api/ActivitiesApi';
import Error from '../../../../components/stateMessage/Error';
import {
  deleteItineraryTemplateAction,
  fetchCityTemplateAction,
  selectAreCityItineraryTemplatesLoading,
  selectCityItineraryTemplates,
  selectHaveCityItineraryTemplatesLoaded,
  setCityTemplateAction,
} from '../../../../reduces/itineraryTemplates';
import { City } from '../../../../types/City';
import { useAppDispatch } from '../../../../store';
import { RootState } from '../../../../reduces/reducer';
import Header from '../Header';
import {
  deleteItineraryTemplates,
  getItineraryTemplates,
} from '../../../../services/templates';
import { useFetchData } from '../../../../infrastructure/hooks/useFetchData';
import { ApiStatus } from '../../../../infrastructure/constants/apiStatus';
import NotFound from '../../../NotFound';

type Props = { history: History<LocationState> };

const IS_MOBILE = isMobileResolution();
const isExpanded = !IS_MOBILE;

type ItemProps = {
  value: Itinerary;
  actions: {
    setSelectedItineraryTemplate(id: string): void;
    onDelete(id: string): void;
  };
};

const Tr = styled.tr`
  &&& {
    td {
      text-align: center;
      vertical-align: middle;
      cursor: pointer;
      i {
        margin: 0 10px;
      }
    }
  }
`;

const Item = memo(({ value, actions: { onDelete } }: ItemProps) => {
  const { vibe, title, id } = value;
  const { url } = useRouteMatch();
  return (
    <Tr key={value.id} className='listrow moveable'>
      <td>
        <Glyphicon glyph='option-vertical' />
      </td>
      <td>{vibe}</td>
      <td>{title}</td>
      <td>
        <Link to={`${url}/${id}`}>
          <i role='link' tabIndex={0} className='fas fa-pen' />
        </Link>
        <i
          role='link'
          tabIndex={-1}
          onClick={() => onDelete(id)}
          className='fas fa-times'
        />
      </td>
    </Tr>
  );
});

const ItineraryTemplatePage = ({ history }: Props) => {
  const dispatch = useAppDispatch();
  const { url } = useRouteMatch();
  const { goBack } = history;
  const { citySlug } = useParams() ?? {};
  const [apiStatus, city, fetchCity] = useFetchData<City>();
  const { name = '' } = city ?? {};
  const itineraryTemplates = useSelector((state: RootState) =>
    selectCityItineraryTemplates(state, citySlug)
  );
  const isTemplateLoading = useSelector((state: RootState) =>
    selectAreCityItineraryTemplatesLoading(state, citySlug)
  );
  const hasTemplateLoaded = useSelector((state: RootState) =>
    selectHaveCityItineraryTemplatesLoaded(state, citySlug)
  );

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const hideMessage = () => {
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 5000);
  };
  const onDelete = async (id: string) => {
    try {
      await deleteItineraryTemplates(citySlug, id);
      dispatch(deleteItineraryTemplateAction(id));
    } catch (e) {
      const error = e as AxiosError;
      setErrorMessage(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      hideMessage();
    }
  };

  useEffect(() => {
    let ignore = false;
    const initCity = async () => {
      if (!ignore) fetchCity(() => getCityBySlug(citySlug));
    };
    initCity();
    return () => {
      ignore = false;
    };
  }, [citySlug, fetchCity]);

  const fetchCityTemplates = useCallback(async () => {
    if (!isTemplateLoading && !hasTemplateLoaded) {
      try {
        dispatch(fetchCityTemplateAction(citySlug));
        const template = await getItineraryTemplates(citySlug);
        dispatch(setCityTemplateAction(citySlug, template));
      } catch (error) {}
    }
  }, [citySlug, dispatch, hasTemplateLoaded, isTemplateLoading]);

  useEffect(() => {
    fetchCityTemplates();
  }, [fetchCityTemplates]);

  const actions = {
    onDelete,
  };

  if (isTemplateLoading || apiStatus === ApiStatus.loading) {
    return (
      <Glyphicon
        glyph='refresh'
        style={{
          display: 'block',
          marginLeft: 'auto',
          width: '50px',
          height: '50px',
          marginRight: 'auto',
        }}
        className='spinning'
      />
    );
  }

  if (apiStatus === ApiStatus.error) {
    return <NotFound />;
  }

  return (
    <>
      {!!errorMessage && <Error text={errorMessage} />}
      <Header goBack={goBack} name={name} />
      <div className='data-container'>
        <div className='data-box'>
          <div className='media'>
            <h2 className='media-heading'>
              <b>{name}</b>
            </h2>
          </div>
        </div>

        <div className='data-box'>
          <h4 className='header'>Itinerary Templates</h4>
          <Link className='btn btn-def' to={`${url}/new`}>
            + Add New
          </Link>
          <CollapsableItem isExpanded={isExpanded}>
            <DragnDropTable
              // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
              // @ts-ignore
              content={itineraryTemplates}
              headers={['Vibe', 'Title', 'Actions']}
              notHeader={IS_MOBILE}
              actions={actions}
              receiver='itineraryTemplates'
              Element={Item}
            />
          </CollapsableItem>
        </div>
      </div>
    </>
  );
};

export default ItineraryTemplatePage;
